<template>
  <v-container>
    <v-row class="py-2" align="center" justify="center">
      <v-col cols="4"
        >Recommended portion: <strong class="navy--text">{{ portionSize || "NOT SET" }}</strong></v-col
      >
      <v-col class="d-flex">
        <v-btn class="mx-2" fab dark x-small color="grey-navy" @click="clickUpdateWeek(-1)">
          <v-icon dark>
            mdi-minus
          </v-icon>
        </v-btn>
        <h2>
          {{ firstDay | moment("DD MMMM") }}
          -
          {{ lastDay | moment("DD MMMM YYYY") }}
        </h2>
        <v-btn class="mx-2" fab dark x-small color="grey-navy" @click="clickUpdateWeek(1)">
          <v-icon dark>
            mdi-plus
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-skeleton-loader v-if="loading || $apollo.loading" type="table" />
    <div v-if="!loading && !$apollo.loading">
      <v-row>
        <v-col cols="12" class="pt-0">
          <v-simple-table>
            <template v-slot:default>
              <tbody v-if="!$apollo.loading">
                <tr v-for="day in days" :key="day">
                  <td class="text-h6 pl-1">
                    {{ day }}
                    <v-icon v-if="getDayRatingIcon(day)" :color="getDayRatingColor(day)">
                      {{ getDayRatingIcon(day) }}
                    </v-icon>
                    <br />
                    <span v-if="getDayRatingText(day)" class="text-body-1">"{{ getDayRatingText(day) }}"</span>
                  </td>
                  <td v-if="nutritionLog.edges.length > 0">
                    <div class="d-flex flex-row">
                      <div v-for="log in getDayNutrition(day)" :key="log.node.id">
                        <v-card width="150px" class="ma-2">
                          <v-tooltip v-if="log.node.computedImage" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-img
                                :src="log.node.computedImage"
                                :lazy-src="log.node.computedImage"
                                :alt="logRecipe(log.node) && logRecipe(log.node).title"
                                :height="80"
                                :width="150"
                                :aspect-ratio="10 / 9"
                                v-bind="attrs"
                                v-on="on"
                              />
                            </template>
                            <v-img
                              :src="log.node.computedImage"
                              :lazy-src="log.node.computedImage"
                              :alt="logRecipe(log.node) && logRecipe(log.node).title"
                              :max-height="580"
                              :max-width="540"
                              :min-height="300"
                              :min-width="300"
                            />
                          </v-tooltip>
                          <div v-else class="py-2 text-center">
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-image-off</v-icon>
                              </template>
                              <span>No image logged</span>
                            </v-tooltip>
                          </div>
                          <v-card-text class="pa-0 pl-2 text-left">
                            <div class="d-flex">
                              <strong class="font-weight-bold pink--text">{{ log.node.meal }}</strong>
                            </div>
                            <v-card-subtitle class="pa-0 navy--text three-lines">
                              <div v-if="log.node.customTitle">
                                <b>{{ log.node.customTitle }}</b>
                              </div>
                              <b v-if="logRecipe(log.node) && logRecipe(log.node).id">Recommended meal: </b>
                              <a
                                v-if="logRecipe(log.node) && logRecipe(log.node).id"
                                :href="`${$appEndPoint}/recipes/${logRecipe(log.node).id}`"
                                target="_blank"
                              >
                                <div>
                                  <span :class="{ 'font-weight-bold text-body-1': !log.node.customTitle }">{{
                                    logRecipe(log.node) && logRecipe(log.node).title
                                  }}</span>
                                  <br />
                                </div>
                              </a>
                              <span v-if="log.node.comments">
                                <b>Comments:</b> {{ log.node.comments || "-" }} <br
                              /></span>

                              <small>Logged: {{ log.node.createdAt | moment("HH:MM, ddd DD MMM") }}</small>
                            </v-card-subtitle>
                          </v-card-text>
                          <v-chip v-if="log.node.onTrack" class="my-2 mx-2" color="nutrition">
                            ON TRACK
                          </v-chip>
                          <v-chip v-if="log.node.flexiMeal" class="my-2 mx-2" color="sleep">
                            FLEXI MEAL
                          </v-chip>

                          <div class="text-center pb-2">
                            <v-btn
                              v-if="coachingChatId"
                              small
                              @click="
                                chatDialog = true;
                                selectedNutritionImage = log.node.computedImage;
                                selectedLogId = log.node.id;
                                selectedCoachComment = log.node.coachComment;
                                selectedLog = log.node;
                              "
                              ><v-icon color="navy">mdi-chat</v-icon></v-btn
                            >
                          </div>
                        </v-card>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </div>
    <v-dialog
      v-if="chatDialog"
      persistent
      v-model="chatDialog"
      width="400"
      class="limited-height"
      transition="dialog-bottom-transition"
    >
      <send-message-card
        @closed="chatDialog = false"
        :coaching-chat-id="coachingChatId"
        :image="selectedNutritionImage"
        :user-id="userId"
        :log-id="selectedLogId"
        log-type="NUTRITION"
        :coach-comment="selectedCoachComment"
        :log="selectedLog"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { NUTRITION_LOG_DETAIL_QUERY } from "@/constants/graphql/nutrition/nutrition";
import { NUTRITION_DAY_RATING_QUERY } from "@/constants/graphql/nutrition/nutrition";
import SendMessageCard from "@/components/chats/SendMessageCard";
import FeelingMixin from "@/components/shared/mixins/FeelingMixin";

export default {
  name: "NutritionLog",
  components: {
    SendMessageCard,
  },
  mixins: [FeelingMixin],
  data() {
    return {
      nutritionMessages: [],
      nutritionLog: [],
      portionSize: null,
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      lastDay: "",
      firstDay: "",
      loading: true,
      coachingChatId: null,
      chatDialog: false,
      selectedNutritionImage: null,
      selectedLogId: null,
      selectedCoachComment: null,
      // passing entire log now
      selectedLog: null,
    };
  },
  props: {
    userId: {
      type: String,
      default: null,
    },
    selectedDate: {
      type: String,
      required: false,
    },
  },
  mounted() {
    this.resetDate();
  },
  watch: {
    userId: function() {
      this.resetDate();
    },
    selectedDate: function() {
      this.resetDate();
    },
  },
  methods: {
    logRecipe(node) {
      return node.subscriptionRecipe?.recipe || node.recipe;
    },
    resetDate() {
      var firstDay = new Date(this.getMonday()).toISOString();
      var lastDay = new Date(this.getMonday());
      lastDay.setDate(lastDay.getDate() + 6);
      lastDay = lastDay.toISOString();
      this.firstDay = firstDay;
      this.lastDay = lastDay;
    },
    clickUpdateWeek(interval) {
      this.$emit("update-week", interval);
      this.updateWeek(interval);
    },
    updateWeek(interval) {
      let value = interval * 7;
      let firstDay = new Date(this.firstDay);
      let lastDay = new Date(this.lastDay);
      firstDay.setDate(firstDay.getDate() + value);
      lastDay.setDate(lastDay.getDate() + value);
      this.firstDay = firstDay;
      this.lastDay = lastDay;
    },
    getMonday() {
      var startDate = new Date();
      if (this.selectedDate) {
        startDate = new Date(this.selectedDate);
      }
      startDate.setHours(0, 0, 0, 0);
      var day = startDate.getDay(),
        diff = startDate.getDate() - day + (day == 0 ? -6 : 1);
      return new Date(startDate.setDate(diff));
    },
    getDayNutrition(day) {
      return this.nutritionLog.edges.filter((log) => this.$moment(log.node.date).format("dddd") == day);
    },
    getDayRating(day) {
      let rating;
      const ratingNode = this.nutritionDayRatings?.edges?.filter(
        (log) => this.$moment(log.node.date).format("dddd") == day
      );
      if (ratingNode && ratingNode?.length > 0) {
        rating = ratingNode[0].node.rating;
      }
      return rating;
    },
    getDayRatingText(day) {
      let text = "";
      const ratingNode = this.nutritionDayRatings?.edges?.filter(
        (log) => this.$moment(log.node.date).format("dddd") == day
      );
      if (ratingNode && ratingNode?.length > 0) {
        text = ratingNode[0].node?.text;
      }
      return text;
    },
    getDayRatingIcon(day) {
      const rating = this.getDayRating(day);
      // in FeelingMixin
      return this.getEmoticonForRating(rating);
    },
    getDayRatingColor(day) {
      const rating = this.getDayRating(day);
      // in FeelingMixin
      return this.getColorForRating(rating);
    },

    getMessageCountColor(count) {
      let countInt = parseInt(count);
      let color = "red";
      if (countInt >= 3) {
        color = "nutrition";
      }
      if (countInt == 2) {
        color = "yellow";
      }

      return color;
    },
  },
  apollo: {
    nutritionLog: {
      query: NUTRITION_LOG_DETAIL_QUERY,
      variables() {
        this.loading = true;
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        if (this.lastDay == "" || this.firstDay == "") {
          this.resetDate();
        }
        return {
          userId: encoded_json_payload,
          endDate: this.$moment(this.lastDay).format("YYYY-MM-DD"),
          startDate: this.$moment(this.firstDay).format("YYYY-MM-DD"),
        };
      },
      fetchPolicy: "no-cache",
      result(response) {
        if (response.data) {
          this.loading = false;
          this.coachingChatId = response.data.user?.userProfile?.coachingChat?.id;
          this.portionSize = response.data.user?.userProfile?.calculatedServingSize;
          return response.data.nutritionLog;
        }
      },
    },
    nutritionDayRatings: {
      query: NUTRITION_DAY_RATING_QUERY,
      variables() {
        this.loading = true;
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        if (this.lastDay == "" || this.firstDay == "") {
          this.resetDate();
        }
        return {
          userId: encoded_json_payload,
          date_Lte: this.$moment(this.lastDay).format("YYYY-MM-DD"),
          date_Gte: this.$moment(this.firstDay).format("YYYY-MM-DD"),
        };
      },
      fetchPolicy: "no-cache",
    },
  },
};
</script>

<style></style>
